// The HTTP client. Same origin by default: the API is served behind the
// same host as the bundle, which is what names the tenant (D-79, D-82).
//
// Session behaviour lives here and nowhere else (D-81, contract table):
//
// * a 401 `token_missing`, `token_invalid` or `token_stale` on an
//   authenticated request triggers one refresh and one retry;
//   `token_stale` also reloads the profile before the retry;
// * every other 401 or 403 is reported to the session layer, which decides
//   the state (revoked, suspended, sign in again, change the password) and
//   never discards the local queue;
// * a 429 carries `Retry-After` so the caller waits before trying again;
// * a request that gets no answer is a NetworkError, distinct from every
//   API answer.
import { ApiError, CODES, NetworkError } from './errors';

export interface RequestOptions {
  /** Attach the bearer token. Public routes set it to false. */
  auth?: boolean;
  /** Body, serialised as JSON. */
  body?: unknown;
  /** INC-05b: a body sent as it is, such as a photo; its type goes in `headers`.
   *  INC-10b: or a form with a file, whose type the browser writes itself. */
  rawBody?: ArrayBuffer | Blob | FormData;
  /** Extra headers, on top of Accept and Content-Type. */
  headers?: Record<string, string>;
  /** INC-11a-1: the answer is a file, read whole with its name. */
  file?: boolean;
}

/** INC-11a-1: a file the API answered, with the name it gave it. */
export interface DownloadedFile {
  blob: Blob;
  filename: string | null;
}

/** The name a Content-Disposition gives, the UTF-8 one first. */
export function filenameOf(disposition: string | null): string | null {
  if (disposition === null) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (encoded !== undefined) {
    try {
      return decodeURIComponent(encoded.trim());
    } catch {
      // A malformed encoding: the plain name, if there is one.
    }
  }
  return /filename="([^"]*)"/i.exec(disposition)?.[1] ?? null;
}

export interface SessionHooks {
  /** The current access token, or null when there is no session. */
  getAccessToken: () => Promise<string | null>;
  /** Rotate the tokens. Resolves to the new access token, or null when the
   *  refresh was refused (the hook has already changed the session state). */
  refresh: () => Promise<string | null>;
  /** The profile behind the token must be reloaded (`token_stale`). */
  onProfileStale: () => Promise<void>;
  /** A 401 or 403 the client does not resolve on its own. */
  onSessionCode: (code: string, status: number) => Promise<void>;
}

export type ConditionalResult<T> =
  { status: 'unchanged' } | { status: 'ok'; body: T; etag: string | null };

export interface ApiClientOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  hooks: SessionHooks;
}

const REFRESHABLE = new Set<string>([CODES.tokenMissing, CODES.tokenInvalid, CODES.tokenStale]);

function parseRetryAfter(header: string | null): number | null {
  if (header === null) return null;
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return Math.max(0, seconds) * 1000;
  const at = Date.parse(header);
  return Number.isNaN(at) ? null : Math.max(0, at - Date.now());
}

interface ErrorBody {
  detail?: unknown;
  code?: unknown;
  count?: unknown;
  ids?: unknown;
}

async function readErrorBody(
  response: Response,
): Promise<{ code: string; detail: string; count: number | null; ids: string[] | null }> {
  let body: ErrorBody | null = null;
  try {
    body = (await response.json()) as ErrorBody;
  } catch {
    body = null;
  }
  const code = typeof body?.code === 'string' ? body.code : `http_${response.status}`;
  const detail = typeof body?.detail === 'string' ? body.detail : response.statusText;
  // INC-11c-2: a refusal that says how many, beside its code.
  const count = typeof body?.count === 'number' ? body.count : null;
  // INC-14a-3d-2b: the reports a refusal of a batch names, beside its code.
  const ids = Array.isArray(body?.ids)
    ? body.ids.filter((one): one is string => typeof one === 'string')
    : null;
  return { code, detail, count, ids };
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly hooks: SessionHooks;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl ?? '';
    this.fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
    this.hooks = options.hooks;
  }

  get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('GET', path, options);
  }

  /** A conditional read: with the entity tag the caller already holds, the
   *  API answers 304 and no body, and the caller keeps what it has. */
  async getConditional<T>(path: string, etag: string | null): Promise<ConditionalResult<T>> {
    const headers: Record<string, string> = {};
    if (etag !== null) headers['If-None-Match'] = etag;
    return this.send<ConditionalResult<T>>('GET', path, { headers }, true, true);
  }

  post<T>(path: string, body: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('POST', path, { ...options, body });
  }

  /** A binary PUT with the session behaviour of every other request (INC-05b). */
  putBinary<T>(
    path: string,
    body: ArrayBuffer | Blob,
    headers: Record<string, string>,
  ): Promise<T> {
    return this.request<T>('PUT', path, { rawBody: body, headers });
  }

  async request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
    return this.send<T>(method, path, options, true);
  }

  /** INC-11a-1: a file, with the session behaviour of every other request. */
  getFile(path: string): Promise<DownloadedFile> {
    return this.request<DownloadedFile>('GET', path, { file: true });
  }

  private async send<T>(
    method: string,
    path: string,
    options: RequestOptions,
    mayRefresh: boolean,
    conditional = false,
  ): Promise<T> {
    const auth = options.auth ?? true;
    const headers: Record<string, string> = {
      Accept: options.file === true ? '*/*' : 'application/json',
      ...options.headers,
    };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (auth) {
      const token = await this.hooks.getAccessToken();
      if (token !== null) headers.Authorization = `Bearer ${token}`;
    }
    let response: Response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method,
        headers,
        body:
          options.rawBody ??
          (options.body === undefined ? undefined : JSON.stringify(options.body)),
      });
    } catch (error) {
      throw new NetworkError(error);
    }
    if (conditional && response.status === 304) {
      return { status: 'unchanged' } as T;
    }
    if (response.ok) {
      if (response.status === 204) return undefined as T;
      if (options.file === true) {
        const file: DownloadedFile = {
          blob: await response.blob(),
          filename: filenameOf(response.headers.get('Content-Disposition')),
        };
        return file as T;
      }
      if (conditional) {
        return {
          status: 'ok',
          body: (await response.json()) as unknown,
          etag: response.headers.get('ETag'),
        } as T;
      }
      return (await response.json()) as T;
    }
    const { code, detail, count, ids } = await readErrorBody(response);
    if (auth && mayRefresh && response.status === 401 && REFRESHABLE.has(code)) {
      const refreshed = await this.hooks.refresh();
      if (refreshed !== null) {
        if (code === CODES.tokenStale) await this.hooks.onProfileStale();
        return this.send<T>(method, path, options, false, conditional);
      }
      throw new ApiError(response.status, code, detail);
    }
    if (response.status === 401 || response.status === 403) {
      await this.hooks.onSessionCode(code, response.status);
    }
    const retryAfterMs =
      response.status === 429 ? parseRetryAfter(response.headers.get('Retry-After')) : null;
    throw new ApiError(response.status, code, detail, retryAfterMs, count, ids);
  }
}
